import { useEffect } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Flag, { FlagGroup } from '@atlaskit/flag';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';

import { ComponentFlagProps } from 'src/redux/flags/types';
import { urls } from 'src/urls';
import {
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';

export const NON_DISMISSIBLE_UNLINKED_WORKSPACE_FLAG_ID =
  'non-dismissible-unlinked-workspace-flag' as const;

const messages = defineMessages({
  title: {
    id: 'frontbucket.flags.unlinkedWorkspace.title',
    description: 'Text for title of flag',
    defaultMessage: 'Link this workspace to an Atlassian organization',
  },
  description: {
    id: 'frontbucket.flags.unlinkedWorkspace.description',
    description: 'Text for description of flag',
    defaultMessage:
      'Link this workspace to an Atlassian organization to take the first step and avoid delays in benefiting from upcoming features like centralized user management and unified billing across your Atlassian products.',
  },
  learnMoreText: {
    id: 'frontbucket.flags.unlinkedWorkspace.learnMoreText',
    description: 'Text for link to learn more',
    defaultMessage: 'Learn more',
  },
  linkWsText: {
    id: 'frontbucket.flags.unlinkedWorkspace.linkWsText',
    description: 'Text for link to link workspace',
    defaultMessage: 'Link workspace',
  },
});

const NonDismissibleUnlinkedWorkspaceFlag = ({
  id,
  ...flagProps
}: ComponentFlagProps) => {
  const intl = useIntl();

  useEffect(() => {
    publishScreenEvent('non-dismissible-unlinked-workspace-flag-shown');
  }, []);

  const onLearnMore = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'learnMore',
      source: 'unlinkedWorkspaceFlag',
    });
  };

  const onLinkWorkspace = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'linkWorkspace',
      source: 'unlinkedWorkspaceFlag',
    });
  };

  const actions = [
    {
      content: intl.formatMessage(messages.learnMoreText),
      href: urls.external.orgToWsLinkingSupportPage,
      target: '_blank',
      onClick: onLearnMore,
    },
    {
      content: intl.formatMessage(messages.linkWsText),
      href: urls.external.orgToWsLinking,
      target: '_blank',
      onClick: onLinkWorkspace,
    },
  ];

  return (
    <FlagGroup>
      <Flag
        {...flagProps}
        actions={actions}
        appearance="warning"
        description={<FormattedMessage {...messages.description} />}
        icon={<StatusWarningIcon spacing="spacious" label="Warning" />}
        id={id}
        title={intl.formatMessage(messages.title)}
      />
    </FlagGroup>
  );
};

// eslint-disable-next-line import/no-default-export
export default NonDismissibleUnlinkedWorkspaceFlag;
